<template>
    <ModalDialogContainer ref="modalContainer" v-model:display="displayStatus">
        <div class="modal-dialog modal-md pm-dialog" role="document" @click="stopPropagationEvent">
            <div class="modal-content">
                <div class="modal-header pm-header">
                    <div class="pm-header-icon">
                        <IconItem icon="mdi:account-circle-outline" height="1.1em" />
                    </div>
                    <div class="modal-title pm-title">
                        <template v-if="loading">{{ $t("Loading profile") }}…</template>
                        <template v-else-if="notFound">{{ $t("User not found") }}</template>
                        <template v-else>{{ name || username }}</template>
                    </div>
                    <button type="button" class="modal-close-btn" :title="$t('Close')" @click="close">
                        <IconItem icon="mdi:close" height="1.1em" />
                    </button>
                </div>

                <div class="modal-body pm-body">
                    <div v-if="loading" class="pm-state-box">
                        <IconItem icon="mdi:loading" height="2em" class="spin" />
                        <span>{{ $t("Loading profile") }}…</span>
                    </div>

                    <div v-else-if="notFound" class="pm-state-box pm-state-error">
                        <IconItem icon="mdi:account-off-outline" height="2.5em" />
                        <span>{{ $t("User not found") }}</span>
                        <span class="pm-state-hint">{{
                            $t("The user you are looking for does not exist or was deleted from the platform.")
                        }}</span>
                    </div>

                    <div v-else class="pm-profile">
                        <div class="pm-avatar-row">
                            <div class="pm-avatar">
                                <img v-if="image" class="pm-avatar-img" :src="image" alt="" />
                                <img v-else class="pm-avatar-img" src="@/assets/user.png" alt="" />
                            </div>
                            <div class="pm-identity">
                                <div class="pm-name">{{ name || username }}</div>
                                <div class="pm-username">@{{ username }}</div>
                            </div>
                        </div>

                        <div class="pm-info-list">
                            <div class="pm-info-row">
                                <IconItem icon="mdi:calendar-outline" height="0.85em" class="pm-info-icon" />
                                <span class="pm-info-label">{{ $t("Join date") }}</span>
                                <span class="pm-info-value">{{ renderDate(joinDate) }}</span>
                            </div>
                            <div v-if="bio" class="pm-info-row">
                                <IconItem icon="mdi:information-outline" height="0.85em" class="pm-info-icon" />
                                <span class="pm-info-label">{{ $t("Bio") }}</span>
                                <span class="pm-info-value">{{ bio }}</span>
                            </div>
                            <div v-if="location" class="pm-info-row">
                                <IconItem icon="mdi:map-marker-outline" height="0.85em" class="pm-info-icon" />
                                <span class="pm-info-label">{{ $t("Location") }}</span>
                                <span class="pm-info-value">{{ location }}</span>
                            </div>
                            <div v-if="website" class="pm-info-row">
                                <IconItem icon="mdi:link-variant" height="0.85em" class="pm-info-icon" />
                                <span class="pm-info-label">{{ $t("Website") }}</span>
                                <a class="pm-info-link" :href="website" target="_blank" rel="noopener noreferrer">{{ website }}</a>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="modal-footer pm-footer">
                    <button type="button" class="pm-btn" @click="close">
                        {{ $t("Close") }}
                    </button>
                </div>
            </div>
        </div>
    </ModalDialogContainer>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { useVModel } from "@/utils/v-model";
import { Timeouts } from "@/utils/timeout";
import { Request } from "@asanrom/request-browser";
import { getUniqueStringId } from "@/utils/unique-id";
import { ApiProfile } from "@/api/api-group-profile";
import { renderDate } from "@/utils/time-utils";

export default defineComponent({
    name: "ProfileModal",
    emits: ["update:display"],
    props: {
        display: Boolean,
        uid: String,
    },
    setup(props) {
        return {
            displayStatus: useVModel(props, "display"),
            loadRequestId: getUniqueStringId(),
        };
    },
    data: function () {
        return {
            loading: true,
            notFound: false,

            username: "",
            joinDate: "",
            name: "",
            bio: "",
            image: "",
            location: "",
            website: "",
        };
    },
    methods: {
        close: function () {
            this.displayStatus = false;
        },

        escapeToClose: function (event) {
            if (event.key === "Escape") {
                this.close();
            }
        },

        stopPropagationEvent: function (e) {
            e.stopPropagation();
        },

        renderDate: function (date: string) {
            return renderDate(date, this.$t);
        },

        load: function () {
            Timeouts.Abort(this.loadRequestId);
            Request.Abort(this.loadRequestId);

            this.loading = true;

            Request.Pending(this.loadRequestId, ApiProfile.GetProfile(this.uid))
                .onSuccess((profile) => {
                    this.loading = false;
                    this.notFound = false;
                    this.image = profile.image;
                    this.name = profile.name;
                    this.username = profile.username;
                    this.joinDate = profile.joinDate;
                    this.bio = profile.bio;
                    this.location = profile.location;
                    this.website = profile.website;
                })
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        unauthorized: () => {
                            this.$requireLogin();
                        },
                        notFound: () => {
                            this.loading = false;
                            this.notFound = true;
                        },
                        temporalError: () => {
                            // Retry
                            Timeouts.Set(this.loadRequestId, 1500, this.load.bind(this));
                        },
                    });
                })
                .onUnexpectedError((err) => {
                    console.error(err);
                    // Retry
                    Timeouts.Set(this.loadRequestId, 1500, this.load.bind(this));
                });
        },
    },
    mounted: function () {
        if (this.display) {
            this.load();
            this.$autoFocus();
        }
    },
    beforeUnmount: function () {
        Timeouts.Abort(this.loadRequestId);
        Request.Abort(this.loadRequestId);
    },
    watch: {
        display: function () {
            if (this.display) {
                this.load();
                this.$autoFocus();
            }
        },
        uid: function () {
            this.load();
        },
    },
});
</script>

<style scoped>
@keyframes pm-spin {
    to {
        transform: rotate(360deg);
    }
}

.pm-header {
    gap: 0.5rem;
}

.pm-header-icon {
    color: var(--blue-terrascribe);
    display: flex;
    align-items: center;
    flex-shrink: 0;
}

.pm-title {
    font-size: 0.95rem !important;
    font-weight: 700 !important;
    padding: 0 !important;
}

.pm-body {
    padding: 1.25rem;
}

.pm-state-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.6rem;
    padding: 2.5rem 1rem;
    color: var(--text-color);
    opacity: 0.5;
    font-size: 0.9rem;
    text-align: center;
}

.pm-state-error {
    opacity: 1;
    color: #ef4444;
}

.pm-state-hint {
    font-size: 0.78rem;
    color: var(--text-color);
    opacity: 0.55;
    max-width: 280px;
    line-height: 1.5;
}

.pm-state-error .pm-state-hint {
    color: #ef4444;
    opacity: 0.7;
}

.pm-profile {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.pm-avatar-row {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.pm-avatar {
    width: 58px;
    height: 58px;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
    border: 2px solid color-mix(in srgb, var(--blue-terrascribe) 40%, transparent);
    background: var(--user-profile-image-background, lightgray);
}

.pm-avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.pm-identity {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    min-width: 0;
}

.pm-name {
    font-size: 1rem;
    font-weight: 700;
    color: var(--text-color);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.pm-username {
    font-size: 0.82rem;
    color: var(--text-color);
    opacity: 0.5;
    font-weight: 500;
}

.pm-info-list {
    display: flex;
    flex-direction: column;
    border: 1px solid var(--theme-border-color);
    border-radius: 10px;
    overflow: hidden;
}

.pm-info-row {
    display: flex;
    align-items: flex-start;
    gap: 0.55rem;
    padding: 0.6rem 0.85rem;
    border-bottom: 1px solid var(--theme-border-color);
    font-size: 0.85rem;
}

.pm-info-row:last-child {
    border-bottom: none;
}

.pm-info-icon {
    color: var(--blue-terrascribe);
    opacity: 0.7;
    flex-shrink: 0;
    margin-top: 0.1em;
}

.pm-info-label {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-color);
    opacity: 0.45;
    flex-shrink: 0;
    min-width: 68px;
    margin-top: 0.1em;
}

.pm-info-value {
    color: var(--text-color);
    flex: 1;
    min-width: 0;
    word-break: break-word;
    line-height: 1.45;
}

.pm-info-link {
    color: var(--blue-terrascribe);
    text-decoration: underline;
    text-underline-offset: 2px;
    word-break: break-all;
    flex: 1;
    min-width: 0;
    transition: opacity 0.12s;
}

.pm-info-link:hover {
    opacity: 0.75;
}

.pm-footer {
    justify-content: flex-end;
}

.pm-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.4rem 1rem;
    font-size: 0.875rem;
    font-weight: 600;
    border-radius: 8px;
    border: 1px solid var(--theme-border-color);
    background: transparent;
    color: var(--text-color);
    cursor: pointer;
    transition: background 0.12s;
}

.pm-btn:hover {
    background: var(--theme-border-color);
}

.spin {
    animation: pm-spin 1s linear infinite;
}
</style>
